import React, { useState } from 'react';
import { Loader2, Inbox, CreditCard, Mail, AlertCircle, Clock, FileBarChart } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboardTab = ({ 
  loadingStats, 
  stats, 
  emails, 
  formatDate,
  getPriorityColor,
  password
}) => {
  const [sendingReport, setSendingReport] = useState(false);

  const sendSalesReport = async (includeHistory) => {
    setSendingReport(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/send-sales-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, include_history: includeHistory })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Sales report emailed successfully');
      } else {
        toast.error(data.detail || 'Failed to send report');
      }
    } catch (error) {
      toast.error('Failed to send report');
    } finally {
      setSendingReport(false);
    }
  };

  if (loadingStats) {
    return (
      <div className="admin-loading" data-testid="dashboard-loading">
        <Loader2 className="h-8 w-8 animate-spin" />
        <p>Loading stats...</p>
      </div>
    );
  }

  if (!stats) {
    return <p className="empty-text">Failed to load stats</p>;
  }

  return (
    <div className="admin-dashboard" data-testid="dashboard-tab">
      {/* Sales Report */}
      <div className="admin-card full-width" style={{ marginBottom: '20px' }}>
        <h2><FileBarChart className="h-5 w-5" /> Fortnightly Sales Report</h2>
        <p className="empty-text" style={{ margin: '8px 0 14px' }}>
          Emailed automatically every 2 weeks (Monday 7:00 AM AEST). Send one now on demand:
        </p>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            className="admin-tab active"
            style={{ cursor: 'pointer' }}
            disabled={sendingReport}
            onClick={() => sendSalesReport(false)}
            data-testid="send-sales-report-btn"
          >
            {sendingReport ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Send Report Now
          </button>
          <button
            className="admin-tab"
            style={{ cursor: 'pointer' }}
            disabled={sendingReport}
            onClick={() => sendSalesReport(true)}
            data-testid="send-sales-report-history-btn"
          >
            {sendingReport ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileBarChart className="h-4 w-4" />}
            Send with Full History
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue">
            <Inbox />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.total_tickets}</span>
            <span className="stat-label">Support Tickets</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <CreditCard />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.total_payments}</span>
            <span className="stat-label">Total Payments</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple">
            <Mail />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.total_emails_sent}</span>
            <span className="stat-label">Emails Sent</span>
          </div>
        </div>
      </div>

      {/* Category & Priority Breakdown */}
      <div className="admin-grid">
        <div className="admin-card">
          <h2><AlertCircle className="h-5 w-5" /> Tickets by Category</h2>
          {Object.keys(stats.tickets_by_category).length > 0 ? (
            <div className="breakdown-list">
              {Object.entries(stats.tickets_by_category).map(([cat, count]) => (
                <div key={cat} className="breakdown-item">
                  <span className="breakdown-label">{cat.replace('_', ' ')}</span>
                  <span className="breakdown-value">{count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-text">No tickets yet</p>
          )}
        </div>
        <div className="admin-card">
          <h2><Clock className="h-5 w-5" /> Tickets by Priority</h2>
          {Object.keys(stats.tickets_by_priority).length > 0 ? (
            <div className="breakdown-list">
              {Object.entries(stats.tickets_by_priority).map(([pri, count]) => (
                <div key={pri} className="breakdown-item">
                  <span 
                    className="breakdown-badge"
                    style={{ background: getPriorityColor(pri) }}
                  >
                    {pri}
                  </span>
                  <span className="breakdown-value">{count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-text">No tickets yet</p>
          )}
        </div>
      </div>

      {/* Recent Emails */}
      <div className="admin-card full-width">
        <h2><Mail className="h-5 w-5" /> Recent Emails Sent</h2>
        {emails.length > 0 ? (
          <div className="recent-list">
            {emails.map((email) => (
              <div key={email._id || email.resend_email_id || `${email.recipient_email}-${email.created_at}`} className="recent-item">
                <div className="recent-info">
                  <span className="recent-email">{email.recipient_email}</span>
                  <span className="recent-type">{email.email_type}</span>
                </div>
                <div className="recent-meta">
                  <span className={`status-badge ${email.status}`}>{email.status}</span>
                  <span className="recent-date">{formatDate(email.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="empty-text">No emails sent yet</p>
        )}
      </div>
    </div>
  );
};

export default AdminDashboardTab;
